import styled from "styled-components";
import PaginatedTable from "../../Componentes/Utils/PaginatedTable";
import { useEffect, useState } from "react";
import useApi from "../../hooks/useApi";
import { useModal } from "../../contexts/ModalContext";
import ModalFiltersControladoria from "./ModalFiltersControladoria";
import ModalItensSolicitacao from "./ModalItensSolicitacao";
import { useToast } from "../../contexts/ToastContext";
import { useRegisterControladoriaPageActions } from "./layout";

const Container = styled.div`
    width: 100%;
    height: 100%;
    overflow: hidden;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
`;

const filtrosIniciais = {
    filial: "",
    numSolicitacao: "",
    dataInicio: "",
    dataFim: ""
};

const Body = styled.div`
    flex: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background-color: var(--ctrl-surface, #fff);
    margin-top: 5px;
    border-radius: 8px;
    border: 1.2px solid var(--ctrl-border, #d3d3d3);
    box-sizing: border-box;
`;

const LinhaTabela = styled.tr`
    cursor: pointer;
`;

const SolicitacoesDeCompra = () => {
    const api = useApi();
    const { notifyError } = useToast();
    const { isModalOpen, openModal, closeModal } = useModal();

    const [pagina, setPagina] = useState(1);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const [dados, setDados] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [solicitacaoSelecionada, setSolicitacaoSelecionada] = useState(null);
    const [itensSolicitacao, setItensSolicitacao] = useState([]);
    const [loadingItens, setLoadingItens] = useState(false);

    const [filtros, setFiltros] = useState(filtrosIniciais);

    const fetchDados = async (page = pagina, curFiltros = filtros) => {
        setLoading(true);

        try {
            const params = new URLSearchParams();
            params.append("page", page);
            params.append("limit", 50);

            if (curFiltros.filial) params.append("filial", curFiltros.filial);
            if (curFiltros.numSolicitacao) params.append("num_solicitacao", curFiltros.numSolicitacao);
            if (curFiltros.dataInicio) params.append("data_inicio", curFiltros.dataInicio);
            if (curFiltros.dataFim) params.append("data_fim", curFiltros.dataFim);

            const { data } = await api.get(`Painel/API/Controladoria/SolicitacoesCompra/index.php?${params.toString()}`);
            setDados(data.data || []);
            setTotalItems(data.quantidade || 0);
            setTotalPages(data.total_pages || 0);
        } catch (e) {
            notifyError(e?.response?.data?.message || "Erro ao carregar solicitações de compra.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDados(pagina, filtros);
    }, [pagina]);

    const refreshData = async () => {
        if (refreshing) return;

        setRefreshing(true);
        await fetchDados(pagina, filtros);
        setRefreshing(false);
    };

    const aplicarFiltros = () => {
        setPagina(1);
        fetchDados(1, filtros);
        closeModal();
    };

    const removerFiltros = () => {
        setFiltros(filtrosIniciais);
        setPagina(1);
        fetchDados(1, filtrosIniciais);
        closeModal();
    };

    const openFilters = () => {
        openModal("controladoria-filtros-solicitacoes");
    };

    useRegisterControladoriaPageActions({
        onRefresh: refreshData,
        onFilter: openFilters,
        refreshing,
    });

    const abrirItensSolicitacao = async (solicitacao) => {
        setSolicitacaoSelecionada(solicitacao);
        setItensSolicitacao([]);
        setLoadingItens(true);
        openModal("controladoria-itens-solicitacao");

        try {
            const params = new URLSearchParams();
            params.append("filial", solicitacao.FILIAL);
            params.append("num_solicitacao", solicitacao.NUM_SOLICITACAO);

            const { data } = await api.get(`Painel/API/Controladoria/SolicitacoesCompra/Itens/index.php?${params.toString()}`);
            setItensSolicitacao(data.data || []);
        } catch (e) {
            notifyError(e?.response?.data?.message || "Erro ao carregar itens da solicitação.");
        } finally {
            setLoadingItens(false);
        }
    };

    return (
        <>
            <Container>
                <Body>
                    <PaginatedTable
                        pagina={pagina}
                        setPagina={setPagina}
                        loading={loading}
                        colunas={4}
                        totalItems={totalItems}
                        totalPages={totalPages}
                        mobileCards={dados.map((item) => ({
                            key: `${item.FILIAL}-${item.NUM_SOLICITACAO}-${item.DT_EMISSAO}`,
                            title: `SC ${item.NUM_SOLICITACAO} · Filial ${item.FILIAL}`,
                            meta: `${item.DT_EMISSAO || "—"} · ${item.SOLICITANTE || "Sem solicitante"}`,
                            onClick: () => abrirItensSolicitacao(item),
                        }))}
                        thead={
                            <tr>
                                <th>FILIAL</th>
                                <th>NÚMERO SOLICITAÇÃO</th>
                                <th>DATA EMISSÃO</th>
                                <th>SOLICITANTE</th>
                            </tr>
                        }
                    >
                        <tbody>
                            {dados.map((item) => (
                                <LinhaTabela
                                    key={`${item.FILIAL}-${item.NUM_SOLICITACAO}-${item.DT_EMISSAO}`}
                                    onClick={() => abrirItensSolicitacao(item)}
                                >
                                    <td>{item.FILIAL}</td>
                                    <td>{item.NUM_SOLICITACAO}</td>
                                    <td>{item.DT_EMISSAO}</td>
                                    <td>{item.SOLICITANTE || " - "}</td>
                                </LinhaTabela>
                            ))}
                        </tbody>
                    </PaginatedTable>
                </Body>
            </Container>

            {isModalOpen("controladoria-filtros-solicitacoes") && (
                <ModalFiltersControladoria
                    fecharModal={closeModal}
                    filtros={filtros}
                    setFiltros={setFiltros}
                    aplicarFiltros={aplicarFiltros}
                    removerFiltros={removerFiltros}
                    fields={[
                        { name: "filial", label: "Filial:", type: "text", id: "filial-sc" },
                        { name: "numSolicitacao", label: "Número Solicitação:", type: "text", id: "numSolicitacao" },
                        { name: "dataInicio", label: "Data Emissão Inicial:", type: "date", id: "data-inicial-sc" },
                        { name: "dataFim", label: "Data Emissão Final:", type: "date", id: "data-final-sc" },
                    ]}
                />
            )}

            {isModalOpen("controladoria-itens-solicitacao") && (
                <ModalItensSolicitacao
                    fecharModal={closeModal}
                    solicitacao={solicitacaoSelecionada}
                    itens={itensSolicitacao}
                    loadingItens={loadingItens}
                />
            )}
        </>
    );
};

export default SolicitacoesDeCompra;
